import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearCredentials } from '../store/slices/authSlice';

export const useInactivityTimer = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get timeout in milliseconds (default 10 minutes)
  // Use useMemo to ensure this recalculates when user.profile.inactivityTimeout changes
  const timeoutDuration = useMemo(
    () => (user?.profile?.inactivityTimeout || 10) * 60 * 1000,
    [user?.profile?.inactivityTimeout]
  );

  const logout = useCallback(() => {
    dispatch(clearCredentials());
    navigate('/');
  }, [dispatch, navigate]);

  const resetTimer = useCallback(() => {
    // Clear existing timer
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }

    // Set new timer
    timeoutId.current = setTimeout(() => {
      logout();
    }, timeoutDuration);
  }, [logout, timeoutDuration]);

  useEffect(() => {
    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Reset timer on any activity
    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [resetTimer]);

  return null;
};
